import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '../lib/cn';
import { DateRangePicker } from './DatePicker';

/**
 * PeriodFilter — Selector de periodo con presets + rango custom.
 */
const PRESETS = [
    { id: 'all',              label: 'Todo el periodo' },
    { id: 'today',            label: 'Hoy' },
    { id: 'last-7d',          label: 'Últimos 7 días' },
    { id: 'last-30d',         label: 'Últimos 30 días' },
    { id: 'last-90d',         label: 'Últimos 90 días' },
    { id: 'month-current',    label: 'Mes actual' },
    { id: 'month-prev',       label: 'Mes anterior' },
    { id: 'quarter-current',  label: 'Trimestre actual' },
    { id: 'year-current',     label: 'Año actual' },
    { id: 'custom',           label: 'Personalizado…' },
];

function computeRange(preset) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (preset) {
        case 'all':
            return { from: null, to: null };
        case 'today':
            return { from: today, to: today };
        case 'last-7d': {
            const from = new Date(today);
            from.setDate(from.getDate() - 6);
            return { from, to: today };
        }
        case 'last-30d': {
            const from = new Date(today);
            from.setDate(from.getDate() - 29);
            return { from, to: today };
        }
        case 'last-90d': {
            const from = new Date(today);
            from.setDate(from.getDate() - 89);
            return { from, to: today };
        }
        case 'month-current':
            return {
                from: new Date(now.getFullYear(), now.getMonth(), 1),
                to: today,
            };
        case 'month-prev': {
            const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const to = new Date(now.getFullYear(), now.getMonth(), 0);
            return { from, to };
        }
        case 'quarter-current': {
            const q = Math.floor(now.getMonth() / 3);
            const from = new Date(now.getFullYear(), q * 3, 1);
            return { from, to: today };
        }
        case 'year-current':
            return {
                from: new Date(now.getFullYear(), 0, 1),
                to: today,
            };
        default:
            return { from: null, to: null };
    }
}

function formatLabel(value) {
    if (!value) return 'Seleccionar periodo';
    if (value.preset && value.preset !== 'custom') {
        return PRESETS.find(p => p.id === value.preset)?.label || 'Seleccionar periodo';
    }
    if (value.from && value.to) {
        return `${format(value.from, 'd MMM yy', { locale: es })} — ${format(value.to, 'd MMM yy', { locale: es })}`;
    }
    return 'Personalizado';
}

export const PeriodFilter = ({ value, onChange, className }) => {
    const [open, setOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setShowCustom(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const handlePreset = (preset) => {
        if (preset === 'custom') {
            setShowCustom(true);
            return;
        }
        const range = computeRange(preset);
        onChange({ preset, ...range });
        setOpen(false);
        setShowCustom(false);
    };

    const handleCustomChange = (range) => {
        onChange({ preset: 'custom', from: range.from, to: range.to });
    };

    return (
        <div className={cn('relative', className)} ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className={cn(
                    'flex items-center gap-2 h-9 px-3',
                    'rounded-lg border border-slate-300 bg-white',
                    'text-sm text-slate-700 hover:border-slate-400 hover:text-slate-900',
                    'transition-colors min-w-[180px]'
                )}
            >
                <Calendar className="size-3.5 text-slate-400" strokeWidth={1.75} />
                <span className="flex-1 text-left truncate">{formatLabel(value)}</span>
                <ChevronDown className="size-3.5 text-slate-400" />
            </button>

            {open && (
                <div className="absolute top-[calc(100%+4px)] right-0 z-40 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden min-w-[200px]">
                    {!showCustom ? (
                        <div className="p-1">
                            {PRESETS.map(preset => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => handlePreset(preset.id)}
                                    className={cn(
                                        'w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors',
                                        value?.preset === preset.id
                                            ? 'bg-[hsl(var(--brand-light))]/10 text-[hsl(var(--brand-light))] font-medium'
                                            : 'text-slate-700 hover:bg-slate-50'
                                    )}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="p-3">
                            <DateRangePicker
                                from={value?.from}
                                to={value?.to}
                                onChange={handleCustomChange}
                            />
                            <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setShowCustom(false)}
                                    className="text-xs text-slate-500 hover:text-slate-700"
                                >
                                    Volver
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setOpen(false); setShowCustom(false); }}
                                    className="text-xs font-medium text-[hsl(var(--brand-light))] hover:text-[hsl(var(--brand-dark))]"
                                >
                                    Aplicar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
